import {Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure} from "@heroui/react";
import _ from 'lodash';
import {twMerge} from 'tailwind-merge';
import {useGlobalAudioPlayer} from 'react-use-audio-player';
import toast from 'react-hot-toast';
import {useState} from 'react';
import {motion, useUnmountEffect} from 'framer-motion';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';

/* Per-voice gradient palette (picked deterministically by id). */
const VOICE_GRADS = [
	['#7c3aed', '#5b21b6'], ['#ec4899', '#9d174d'], ['#3b6ef5', '#1733c4'],
	['#16c098', '#0a8f6c'], ['#f6a609', '#d97706'], ['#6366f1', '#4338ca'],
	['#fb7185', '#be123c'], ['#0ea5e9', '#0369a1'], ['#14b8a6', '#0f766e'],
];

/* Deterministic "waveform signature" — stable bar heights (0.18–1) per voice id. */
function waveHeights(seed, n = 32) {
	let h = (Math.abs(Number(seed) || 1) * 2654435761) >>> 0;
	const out = [];
	for (let i = 0; i < n; i++) {
		h = (h * 1103515245 + 12345) >>> 0;
		out.push(0.18 + ((h % 1000) / 1000) * 0.82);
	}
	return out;
}

/* Per-voice waveform strip — full-bleed to the card edges; flows like a live wave
   while playing, and fades toward zero (bottom-anchored) when idle. */
function Waveform({heights, color, playing}) {
	return (
		<div className={`-mx-[14px] -mb-[14px] flex items-end gap-[2px] h-7 [mask-image:linear-gradient(to_top,#000_40%,transparent)] [-webkit-mask-image:linear-gradient(to_top,#000_40%,transparent)] transition-opacity ${playing ? 'opacity-100' : 'opacity-[0.12]'}`}>
			{heights.map((h, i) => (
				playing
					? <motion.span key={i} className="flex-1 rounded-t-full origin-bottom" style={{height: '100%', background: color}}
						animate={{scaleY: [h, Math.min(1, h + 0.4), h * 0.55, h]}}
						transition={{duration: 1, repeat: Infinity, ease: 'easeInOut', delay: i * 0.035}} />
					: <span key={i} className="flex-1 rounded-t-full origin-bottom" style={{height: '100%', transform: `scaleY(${h})`, background: color}} />
			))}
		</div>
	);
}

export default function VoiceCard({data, playAudio, flat = false, ...props}) {
	const {stop, isLoading, playing, src, load} = useGlobalAudioPlayer();
	const [isLoadingAudio, setIsLoading] = useState(false);
	const [isMoved, setIsMoved] = useState(false);
	const queryClient = useQueryClient()
	const {isOpen:deleteModalIsOpen,  onOpenChange:deleteModalOnOpenChange} = useDisclosure()
	const [selectedVoiceId, setSelectedVoiceId] = useState(null);


	const grad = VOICE_GRADS[Math.abs(Number(data?.id) || 0) % VOICE_GRADS.length];
	const isPremium = _.lowerCase(data?.speaker_type || 'standard') === 'premium';
	const isThisPlaying = src === data?.speaker_voice && playing;
	const noAudio = _.isEmpty(data?.speaker_voice);
	const heights = waveHeights(data?.id);

	const handlePlay = async (audioFilePath = data?.speaker_voice) => {
		if (!_.startsWith(audioFilePath, 'http')) {
			toast.error(`Invalid audio file`);
			return;
		}
		stop();
		setIsLoading(true);
		try {
			const response = await fetch(audioFilePath)
			if (response?.ok) {
				const filePath = URL.createObjectURL(await response.blob());
				await load(audioFilePath, {
					autoplay: true,
					onload: () => {
						setIsLoading(false);
					}
				})
			} else {
				toast.error('This voice is not available now. Please try again later.');
				setIsLoading(false);
			}
		} catch (error) {
			toast.error('This voice is not available now. Please try again later.');
			//toast.error(error.message);
			setIsLoading(false);
		}
	}
	
	useUnmountEffect(() => {
		stop();
	}, [])
	
	const onBookmark = useMutation({
		mutationKey: `add_bookmark_speaker_${data?.id}`,
		onMutate: () => setIsMoved(prevState => !prevState),
		mutationFn: () => repositories.addBookmarkSpeaker(data?.id),
		onSuccess: async (response) => {
			toast.success(!data?.is_bookmarked ? 'Voice successfully bookmarked.' : 'Voice successfully remove to bookmarked.' || response?.detail || 'Speaker bookmark complete.')
			await queryClient.invalidateQueries(['voiceList'])
			// Auto-dub voice picker uses its own query key — refresh it too so the
			// Starred tab reflects the change immediately.
			await queryClient.invalidateQueries({ queryKey: ['autoDubVoicePicker'] })
		}
	})

	const deleteCloneVoice = useMutation({
		mutationKey: ['delete-clone-voice'],
		mutationFn: () => repositories.project.deleteCloneVoice(selectedVoiceId),
		onSuccess: (response) => {
			queryClient.refetchQueries({
				queryKey :  ['myVoiceList', 'own']
			})
			toast.success("Voice successfully deleted.");
			setSelectedVoiceId(null);
			deleteModalOnOpenChange();
		},
		onError: (error) => {
			deleteModalOnOpenChange();
			toast.error(error?.response?.data?.detail || 'Something went wrong');
		}
	})

	return (
		<>
			<motion.div
				initial={flat ? false : {opacity: 0, y: 12}}
				animate={flat ? undefined : {opacity: 1, y: 0}}
				whileHover={flat ? undefined : {y: -4}}
				transition={{type: 'spring', stiffness: 260, damping: 24}}
				className={twMerge(
					'group relative flex flex-col gap-3.5 bg-white p-4 rounded-3xl border transition-shadow overflow-hidden',
					isThisPlaying
						? 'border-primary/40 shadow-[0_18px_44px_-22px_rgba(90,10,224,0.45)]'
						: 'border-slate-200/80 shadow-[0_10px_40px_-28px_rgba(15,23,42,0.35)] hover:shadow-[0_20px_46px_-26px_rgba(90,10,224,0.3)]'
				)}>
				{/* identity glow tinted by the voice gradient */}
				<span aria-hidden className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full blur-2xl transition-opacity"
					style={{background: grad[0], opacity: isThisPlaying ? 0.22 : 0.1}} />

				<div className="relative flex items-center gap-3.5">
					{/* play avatar */}
					<button
						type="button"
						disabled={noAudio || isLoadingAudio}
						onClick={() => (isThisPlaying ? stop() : handlePlay(data?.speaker_voice))}
						aria-label={isThisPlaying ? 'Pause' : 'Play'}
						className="relative flex-none w-[56px] h-[56px] rounded-full overflow-hidden grid place-items-center text-white disabled:opacity-60 disabled:cursor-not-allowed"
						style={{background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`}}>
						{isThisPlaying && (
							<motion.span aria-hidden className="absolute inset-0 rounded-full ring-2 ring-white/60"
								animate={{opacity: [0.7, 0.2, 0.7], scale: [1, 1.06, 1]}}
								transition={{duration: 1.4, repeat: Infinity, ease: 'easeInOut'}} />
						)}
						<span className="relative z-10">
							{isLoadingAudio
								? <span className="block h-5 w-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
								: <i className={`hgi-stroke ${isThisPlaying ? 'hgi-pause' : 'hgi-play'} text-2xl drop-shadow transition-transform group-hover:scale-110`} />}
						</span>
						<span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
					</button>

					{/* info */}
					<div className="min-w-0 flex-1">
						<h4 className="font-semibold text-slate-800 truncate">{data?.speaker_name || 'Untitled'}</h4>
						<div className="flex gap-1.5 flex-wrap mt-1.5">
							<span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[11.5px] font-semibold px-2.5 py-1">
								{_.startCase(data?.language || 'Unknown')}
							</span>
							{isPremium
								? <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-[11.5px] font-semibold px-2.5 py-1 shadow-sm">
									<i className="hgi-stroke hgi-star text-[11px]" /> Premium
								</span>
								: <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-500 text-[11.5px] font-semibold px-2.5 py-1 capitalize">
									{data?.speaker_type || 'Standard'}
								</span>}
						</div>
					</div>

					{/* actions */}
					<div className="flex-none flex items-center gap-2">
						{(data?.is_custom && data?.created_by) && (
							<Button
								onPress={() => { deleteModalOnOpenChange(); setSelectedVoiceId(data?.id); }}
								isIconOnly color="danger" variant="flat" size="sm" radius="full"
								aria-label="Delete voice">
								<i className="fa-solid fa-trash text-[13px]" />
							</Button>
						)}

						{!data?.is_custom && (
							<Button
								isLoading={onBookmark.isPending}
								onPress={onBookmark.mutate}
								isDisabled={data?.is_custom}
								isIconOnly
								color={data?.is_bookmarked ? 'warning' : 'default'}
								variant={data?.is_bookmarked ? 'solid' : 'flat'}
								size="sm" radius="full"
								aria-label={data?.is_bookmarked ? 'Remove bookmark' : 'Bookmark voice'}>
								<i className="hgi-stroke hgi-star" />
							</Button>
						)}
					</div>
				</div>

				{/* waveform signature */}
				<Waveform heights={heights} color={grad[0]} playing={isThisPlaying} />
			</motion.div>

			<Modal isOpen={deleteModalIsOpen} onOpenChange={deleteModalOnOpenChange}>
				<ModalContent>
					{(onClose) => (
						<>
							<ModalHeader>
								<p>Are you sure?</p>
							</ModalHeader>
							<ModalBody>
								<p>Your voice will be permanently deleted.</p>
							</ModalBody>
							<ModalFooter>
								<Button className="bg-[#c8ceda4d]" onPress={deleteModalOnOpenChange}>Cancel</Button>
								<Button isLoading={deleteCloneVoice.isPending} className="bg-red-500 text-white" onPress={()=>{
									deleteCloneVoice.mutate()
								}}>Confirm</Button>
							</ModalFooter>
						</>
					)}
				</ModalContent>
			</Modal>
		</>
	)
}
